import { FC, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate } from '../../../helpers/general.helpers';

export type OrdersFilterValues = {
  vendorId?: number | '';
  status?: string;
  from?: string;
  till?: string;
};

interface Props {
  initial: OrdersFilterValues;
  vendors: { id: number; name: string }[];
  statuses: { value: string; label: string }[];
  onApply: (value: OrdersFilterValues) => void;
  toggle: () => void;
}

const CalendarIcon = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" className="text-neutral-500">
    <path
      d="M5.333 1.333v2M10.667 1.333v2M2.333 6.053h11.334M14 5.667v5.666c0 2-1 3.334-3.333 3.334H5.333C3 14.667 2 13.333 2 11.333V5.667c0-2 1-3.334 3.333-3.334h5.334C13 2.333 14 3.667 14 5.667z"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const selectClass =
  'appearance-none bg-subtle border border-slate-medium rounded-lg px-3 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary w-full bg-no-repeat bg-[length:12px] bg-[position:right_12px_center] bg-chevron-down pr-10';

const datePickerInputClass =
  'w-full border border-slate-medium bg-subtle rounded-lg pl-3 pr-10 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary';

export const OrdersFilter: FC<Props> = ({ initial, vendors, statuses, onApply, toggle }) => {
  const [value, setValue] = useState<OrdersFilterValues>(initial);

  const apply = () => {
    onApply(value);
    toggle();
  };

  const clear = () => {
    const cleared: OrdersFilterValues = { vendorId: '', status: '', from: undefined, till: undefined };
    setValue(cleared);
    onApply(cleared);
    toggle();
  };

  return (
    <div className="bg-white p-6 rounded-xl w-[420px]">
      <p className="semibold text-lg text-text mb-5">Filter orders</p>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col">
          <label className="medium text-sm text-text mb-1.5" htmlFor="vendor">Vendor</label>
          <select
            id="vendor"
            value={value.vendorId ?? ''}
            onChange={(e) => setValue({ ...value, vendorId: e.target.value ? Number(e.target.value) : '' })}
            className={selectClass}
          >
            <option value="">All vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="medium text-sm text-text mb-1.5" htmlFor="status">Status</label>
          <select
            id="status"
            value={value.status ?? ''}
            onChange={(e) => setValue({ ...value, status: e.target.value || undefined })}
            className={selectClass}
          >
            <option value="">All statuses</option>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="medium text-sm text-text mb-1.5" htmlFor="from">From</label>
            <div className="relative">
              <DatePicker
                id="from"
                selected={value.from ? new Date(value.from) : null}
                onChange={(d) => setValue({ ...value, from: d ? formatDate(d) : undefined })}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className={datePickerInputClass}
                wrapperClassName="w-full block"
                maxDate={value.till ? new Date(value.till) : undefined}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <CalendarIcon />
              </span>
            </div>
          </div>
          <div className="flex flex-col">
            <label className="medium text-sm text-text mb-1.5" htmlFor="till">To</label>
            <div className="relative">
              <DatePicker
                id="till"
                selected={value.till ? new Date(value.till) : null}
                onChange={(d) => setValue({ ...value, till: d ? formatDate(d) : undefined })}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className={datePickerInputClass}
                wrapperClassName="w-full block"
                minDate={value.from ? new Date(value.from) : undefined}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <CalendarIcon />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end items-center gap-3">
        <button
          type="button"
          onClick={clear}
          className="px-5 py-2.5 text-sm medium border border-slate-medium rounded-lg text-text bg-white hover:bg-gray-50 transition-colors"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={apply}
          className="px-5 py-2.5 text-sm medium rounded-lg text-white bg-primary hover:opacity-90 transition-colors"
        >
          Apply filters
        </button>
      </div>
    </div>
  );
};
